import React from "react";
import { useTranslationHook } from "../../../hooks";
import { useDispatch } from "react-redux";
import {
  Box,
  IconButton,
  Toolbar,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { enqueueSnackbar } from "notistack";
import { formatDate } from "../../../utils/formatDate";
import { setLoading } from "../../../store/UI";
import { getProcedure, listTenders, selectTender } from "../../../api/procedure";
import { getAccountEmails } from "../../../api/account";
import { sendEmail } from "../../../api/email/sendEmail";
import { Account } from "../../../types/model/account";
import { Procedure, ProcedureRole } from "../../../types/model/procedure";
import RecipientSelectionDialog from "../../Generic/RecipientSelectionDialog";
import ConfirmationDialog from "../../Generic/ConfirmationDialog";
import { listManagers } from "../../../api/user/listManagers";

interface TenderAccountEmails {
  accountId: number;
  accountName: string;
  emails: string[];
}

interface TenderListProps {
  accountId: number;
  projectId: number;
  activityId: number;
  procedureId: number;
  procedureRole: ProcedureRole;
  procedure: Procedure;
}

interface EnhancedTableToolbarProps {
  onRequestRefresh: () => void;
  winnerName?: string | null;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const { t } = useTranslationHook();
  const { onRequestRefresh, winnerName } = props;

  return (
    <Toolbar
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        {winnerName && (
          <>
            {t("tender.winner")}: <strong>{winnerName}</strong>
          </>
        )}
        {!winnerName && <>{t("tender.no-winner")}</>}
      </Box>

      <Tooltip title={t("generic.refresh")}>
        <IconButton onClick={onRequestRefresh}>
          <CachedIcon />
        </IconButton>
      </Tooltip>
    </Toolbar>
  );
};

const WinnerSelector = (props: TenderListProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();

  const [tenderList, setTenderList] = React.useState<Account[]>([]);
  const [winnerId, setWinnerId] = React.useState<number | null>(null);
  const [winnerName, setWinnerName] = React.useState<string | null>(null);

  const [isNotificationRecipientDialogOpen, setIsNotificationRecipientDialogOpen] = React.useState<boolean>(false);
  const [isWinnerConfirmationDialogOpen, setIsWinnerConfirmationDialogOpen] = React.useState<boolean>(false);
  const [tenderToSelect, setTenderToSelect] = React.useState<{ id: number; name: string } | null>(null);
  const [currentProcedure, setCurrentProcedure] = React.useState<Procedure | null>(null);
  const [selectedRecipientEmails, setSelectedRecipientEmails] = React.useState<string[]>([]);
  const [tenderAccountEmails, setTenderAccountEmails] = React.useState<TenderAccountEmails[]>([]);

  const { accountId, projectId, activityId, procedureId, procedureRole, procedure } =
    props;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const sendNotifications = async (selectedRecipients: string[]) => {
    if (!currentProcedure) return;
    dispatch(setLoading(true));
    try {
      const winner = tenderAccountEmails.find((t) => t.accountId === winnerId);
      const losers = tenderAccountEmails.filter((t) => t.accountId !== winnerId);

      const emailPromises: Promise<any>[] = [];

        if (winner) {
          const winnerEmails = winner.emails.filter((email) =>
            selectedRecipients.includes(email)
          );

          const managers = await listManagers(
            accountId,
            projectId,
            activityId,
            procedureId
          );

          const contactManager = managers.find(
            (manager) => manager.id === procedure.contactManagerId
          );

          const managerEmails = contactManager
            ? [contactManager.email]
            : managers.map(m => m.email);

          if (winnerEmails.length > 0) {
            emailPromises.push(
              sendEmail(
                {
                  to: winnerEmails,
                  cc: managerEmails,
                  template: "procedure_award",
                  variables: {
                    procedureName: currentProcedure.name,
                    accountName: winner.accountName,
                    customMessage: "",
                    isWinner: "true",
                  },
                },
                null
              )
            );
          }
        }

        losers.forEach((loser) => {
          const loserEmails = loser.emails.filter((email) =>
            selectedRecipients.includes(email)
          );
          loserEmails.forEach((recipient) => {
            emailPromises.push(
              sendEmail(
                {
                  to: [recipient],
                  cc: [],
                  template: "procedure_award",
                  variables: {
                    procedureName: currentProcedure.name,
                    accountName: loser.accountName,
                    customMessage: "",
                    isWinner: "false",
                  },
                },
                null
              )
            );
          });
        });

      await Promise.all(emailPromises);
    } finally {
      dispatch(setLoading(false));
    }
  };


  const fetchTenderList = () => {
    try {
      dispatch(setLoading(true));
      listTenders(projectId, activityId, procedureId)
        .then((response) => {
          setTenderList(response);
          const currentWinner = response.find((t) => t.selected);
          setWinnerId(currentWinner?.id || null);
          setWinnerName(currentWinner?.name || null);
          enqueueSnackbar(t("tender.tenders-loaded"), {
            variant: "success",
          });
        })
        .catch(() => {
          enqueueSnackbar(t("error.tenders-loaded-error"), {
            variant: "error",
          });
        })
        .finally(() => {
          dispatch(setLoading(false));
        });
    } catch (error) {
      enqueueSnackbar(t("error.tenders-loaded-error"), {
        variant: "error",
      });
    }
  };

  const handleRefresh = () => {
    fetchTenderList();
  };

  const handleSelectTender = async (tenderId: number, tenderName: string) => {
    try {
      await selectTender(projectId, activityId, procedureId, tenderId);
      enqueueSnackbar(t("tender.select-success"), { variant: "success" });
      setWinnerId(tenderId);
      setWinnerName(tenderName);
      fetchTenderList();

      const procedure = await getProcedure(projectId, activityId, procedureId);
      setCurrentProcedure(procedure);

      if (procedure.started) {
        const tenders = await listTenders(projectId!, activityId!, procedureId!);
        const tenderEmailsData: TenderAccountEmails[] = await Promise.all(
          tenders.map(async (tender) => {
            const emails = await getAccountEmails(tender.id);
            return {
              accountId: tender.id,
              accountName: tender.name,
              emails: emails,
            };
          })
        );
        setTenderAccountEmails(tenderEmailsData);
        setIsNotificationRecipientDialogOpen(true);
      }
    } catch (error) {
      enqueueSnackbar(t("error.tender-select-error"), { variant: "error" });
    }
  };

  React.useEffect(() => {
    fetchTenderList();
  }, [accountId]);

  return (
    <>
      <Paper sx={{ mt: 2, p: 1 }}>
        <EnhancedTableToolbar
          onRequestRefresh={handleRefresh}
          winnerName={winnerName}
        />
      </Paper>

      {(procedureRole.isManager || procedureRole.isOwner) && (
        <>
          <Paper sx={{ mt: 2, p: 1 }}>
            <TableContainer>
              <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">
                      <strong>{t("admin.accounts.name")}</strong>
                    </TableCell>
                    <TableCell align="left">
                      <strong>{t("admin.accounts.created-on")}</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenderList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((tender) => (
                      <TableRow key={tender.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            {tender.name}
                            {tender.id === winnerId && (
                              <EmojiEventsIcon color="warning" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatDate(new Date(tender.createdOn).toISOString())}
                        </TableCell>
                        <TableCell align="right">
                          {procedureRole.isManager && winnerId === null && !procedure.canceled && !procedure.closed && (
                              <IconButton
                                onClick={() => {
                                  setTenderToSelect({
                                    id: tender.id,
                                    name: tender.name,
                                  });
                                  setIsWinnerConfirmationDialogOpen(true);
                                }}
                              >
                                <EmojiEventsIcon />
                              </IconButton>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ mt: 2, p: 1 }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={tenderList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </>
      )}

      <RecipientSelectionDialog
        open={isNotificationRecipientDialogOpen}
        title={t("tender.send-notification-title")}
        tenderAccountEmails={tenderAccountEmails}
        selectedEmails={selectedRecipientEmails}
        onSelectionChange={(emails) => setSelectedRecipientEmails(emails)}
        onConfirm={async () => {
          await sendNotifications(selectedRecipientEmails);
          setIsNotificationRecipientDialogOpen(false);
        }}
        onCancel={() => {
          setIsNotificationRecipientDialogOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isWinnerConfirmationDialogOpen}
        title={t("tender.winner-confirmation-title")}
        message={
          t("tender.winner-confirmation-message-prefix") +
          (tenderToSelect?.name || "") +
          t("tender.winner-confirmation-message-suffix")
        }
        onConfirm={() => {
          if (tenderToSelect) {
            handleSelectTender(tenderToSelect.id, tenderToSelect.name);
          }
          setIsWinnerConfirmationDialogOpen(false);
          setTenderToSelect(null);
        }}
        onCancel={() => {
          setIsWinnerConfirmationDialogOpen(false);
          setTenderToSelect(null);
        }}
      />
    </>
  );
};

export default WinnerSelector;
